import React, { useState } from 'react';
import {
    IconButton,
    Badge,
    Menu,
    MenuItem,
    ListItemText,
    Typography,
    Divider,
    Box,
    Tooltip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '@/contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { keyframes } from '@emotion/react';

const MAX_VISIBLE_NOTIFICATIONS = 50;
const MENU_WIDTH = 380;
const CONTENT_MAX_HEIGHT = 320;

/* Matches .dropdown-menu in Header.css so the bell's popup and the bar's own
   menus are the same object. Radius, border and shadow come from the theme
   (theme/muiTheme.ts); only the size is local. */
const MENU_PAPER_PROPS = {
    sx: {
        width: MENU_WIDTH,
        maxHeight: 440,
        overflow: 'hidden',
        mt: 1.25,
    },
} as const;

const tada = keyframes`
  from {
    transform: scale3d(1, 1, 1);
  }
  10%, 20% {
    transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, -3deg);
  }
  30%, 50%, 70%, 90% {
    transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);
  }
  40%, 60%, 80% {
    transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);
  }
  to {
    transform: scale3d(1, 1, 1);
  }
`;

type Notification = {
    id: string | number;
    title: string;
    message: string;
    created_ts: string;
    is_actionable: boolean;  // Fixed property name
    action_url?: string;
    notification_type?: string;
    is_read: boolean;
    is_completed: boolean;
    last_reminded_at?: string;
};

const truncate = (str: string, n: number): string =>
    str?.length > n ? str.slice(0, n - 1) + '…' : str;

// Helper function to safely render HTML content
const sanitizeAndRenderHTML = (htmlString: string) => {
    // Basic sanitization - you might want to use a library like DOMPurify for production
    return { __html: htmlString };
};

const NotificationsDropdown: React.FC = () => {
    const [actionableAnchorEl, setActionableAnchorEl] = useState<null | HTMLElement>(null);
    const [otherAnchorEl, setOtherAnchorEl] = useState<null | HTMLElement>(null);
    const { actionableNotifications, otherNotifications, refreshNotifications } = useNotification();
    const navigate = useNavigate();


    // Notifications are not polled, so opening a menu is when the list must be current.
    const handleActionableOpen = (event: React.MouseEvent<HTMLElement>) => {
        setActionableAnchorEl(event.currentTarget);
        refreshNotifications();
    };
    const handleActionableClose = () => {
        setActionableAnchorEl(null);
    };

    const handleOtherOpen = (event: React.MouseEvent<HTMLElement>) => {
        setOtherAnchorEl(event.currentTarget);
        refreshNotifications();
    };
    const handleOtherClose = () => {
        setOtherAnchorEl(null);
    };

    const handleRedirect = (notification: Notification) => {
        const tab = notification.is_actionable ? 'actionable' : 'all';
        navigate(`/notifications?days=30&tab=${tab}`);
        handleActionableClose();
        handleOtherClose();
    };

    const handleViewAll = (tab: string) => {
        navigate(`/notifications?days=30&tab=${tab}`);
        handleActionableClose();
        handleOtherClose();
    };

    const visibleActionableNotifications = actionableNotifications.slice(0, MAX_VISIBLE_NOTIFICATIONS);
    const visibleOtherNotifications = otherNotifications.slice(0, MAX_VISIBLE_NOTIFICATIONS);

    return (
        <Box>
            {actionableNotifications.length > 0 && (
                <Tooltip title="Actionable Notifications">
                    <IconButton
                        size="large"
                        aria-label="show new actionable notifications"
                        color="inherit"
                        onClick={handleActionableOpen}
                        sx={{
                            animation: `${tada} 2s ease-in-out`,
                            outline: 'none',
                        }}
                    >
                        <Badge badgeContent={actionableNotifications.length} color="error">
                            <PlaylistAddCheckIcon />
                        </Badge>
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Notifications">
                <IconButton
                    size="large"
                    aria-label="show new notifications"
                    color="inherit"
                    onClick={handleOtherOpen}
                    sx={{
                        animation: otherNotifications.length > 0 ? `${tada} 2s ease-in-out` : 'none',
                        outline: 'none',
                    }}
                >
                    <Badge badgeContent={otherNotifications.length} color="primary">
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            {/* Actionable Notifications Menu */}
            <Menu
                anchorEl={actionableAnchorEl}
                open={Boolean(actionableAnchorEl)}
                onClose={handleActionableClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={MENU_PAPER_PROPS}
                MenuListProps={{ sx: { padding: 0 } }}
            >
                <Box
                    sx={{
                        px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        bgcolor: 'var(--surface-2)', borderBottom: '1px solid var(--border-soft)',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: 'var(--text-faint)',
                        }}
                    >
                        Actionable Notifications
                    </Typography>
                </Box>
                <Box sx={{ maxHeight: CONTENT_MAX_HEIGHT, overflowY: 'auto', px: 0.75, py: 0.5 }}>
                    {visibleActionableNotifications.length === 0 ? (
                        <MenuItem disabled>
                            <ListItemText
                                primary="No new actionable notifications"
                                secondary={actionableNotifications.length > 0 ? "All actionable items may be completed" : ""}
                            />
                        </MenuItem>
                    ) : (
                        <>
                            {visibleActionableNotifications.map((notification, index) => (
                                <React.Fragment key={notification.id}>
                                    <MenuItem
                                        onClick={() => handleRedirect(notification)}
                                        sx={{
                                            my: 1,
                                            whiteSpace: 'normal',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>

                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {truncate(notification.title, 50) || 'Notification'}
                                                    </Box>
                                                }
                                                secondary={
                                                    <>
                                                        <Box
                                                            sx={{
                                                                '& a': {
                                                                    color: 'primary.main',
                                                                    textDecoration: 'underline',
                                                                },
                                                            }}
                                                            dangerouslySetInnerHTML={sanitizeAndRenderHTML(truncate(notification.message, 50))}
                                                        />
                                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                            {formatDistanceToNow(new Date(notification.created_ts))} ago
                                                            {notification.last_reminded_at && (
                                                                <>
                                                                    {' • '}
                                                                    <NotificationsActiveIcon
                                                                        sx={{
                                                                            fontSize: '0.75rem',
                                                                            color: 'warning.main',
                                                                            verticalAlign: 'middle',
                                                                            mr: 0.5
                                                                        }}
                                                                    />
                                                                    Reminded {formatDistanceToNow(new Date(notification.last_reminded_at))} ago
                                                                </>
                                                            )}
                                                        </Typography>
                                                    </>
                                                }
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 'medium',
                                                    whiteSpace: 'normal',
                                                }}
                                                secondaryTypographyProps={{
                                                    component: 'div',
                                                    fontSize: '0.75rem',
                                                    whiteSpace: 'normal',
                                                }}
                                            />
                                        </Box>
                                    </MenuItem>
                                    {index !== visibleActionableNotifications.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </>
                    )}
                </Box>
                <Divider />
                <MenuItem onClick={() => handleViewAll('actionable')} sx={{
                        justifyContent: 'center', py: 1.25, fontWeight: 600,
                        color: 'var(--accent)', borderTop: '1px solid var(--border-soft)',
                        borderRadius: 0,
                    }}>
                    <Typography variant="body2" color="primary">View all actionable notifications</Typography>
                </MenuItem>
            </Menu>

            {/* Other Notifications Menu */}
            <Menu
                anchorEl={otherAnchorEl}
                open={Boolean(otherAnchorEl)}
                onClose={handleOtherClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={MENU_PAPER_PROPS}
                MenuListProps={{ sx: { padding: 0 } }}
            >
                <Box
                    sx={{
                        px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        bgcolor: 'var(--surface-2)', borderBottom: '1px solid var(--border-soft)',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: 'var(--text-faint)',
                        }}
                    >
                        Notifications
                    </Typography>
                </Box>
                <Box sx={{ maxHeight: CONTENT_MAX_HEIGHT, overflowY: 'auto', px: 0.75, py: 0.5 }}>
                    {visibleOtherNotifications.length === 0 ? (
                        <MenuItem disabled>
                            <ListItemText primary="No new notifications" />
                        </MenuItem>
                    ) : (
                        <>
                            {visibleOtherNotifications.map((notification, index) => (
                                <React.Fragment key={notification.id}>
                                    <MenuItem
                                        onClick={() => handleRedirect(notification)}
                                        sx={{
                                            my: 1,
                                            whiteSpace: 'normal',
                                            cursor: 'pointer',
                                            bgcolor: !notification.is_read ? 'var(--accent-soft)' : 'transparent',
                                        }}
                                    >
                                        <ListItemText
                                            primary={truncate(notification.title, 50) || 'Notification'}
                                            secondary={
                                                <>
                                                    <Box
                                                        sx={{
                                                            '& a': {
                                                                color: 'primary.main',
                                                                textDecoration: 'underline',
                                                            },
                                                        }}
                                                        dangerouslySetInnerHTML={sanitizeAndRenderHTML(truncate(notification.message, 50))}
                                                    />
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                        {formatDistanceToNow(new Date(notification.created_ts))} ago
                                                    </Typography>
                                                </>
                                            }
                                            primaryTypographyProps={{
                                                fontSize: '0.875rem',
                                                fontWeight: 'medium',
                                                whiteSpace: 'normal',
                                            }}
                                            secondaryTypographyProps={{
                                                component: 'div',
                                                fontSize: '0.75rem',
                                                whiteSpace: 'normal',
                                            }}
                                        />
                                    </MenuItem>
                                    {index !== visibleOtherNotifications.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </>
                    )}
                </Box>
                <Divider />
                <MenuItem onClick={() => handleViewAll('all')} sx={{
                        justifyContent: 'center', py: 1.25, fontWeight: 600,
                        color: 'var(--accent)', borderTop: '1px solid var(--border-soft)',
                        borderRadius: 0,
                    }}>
                    <Typography variant="body2" color="primary">View all notifications</Typography>
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default NotificationsDropdown;